import { createRxDatabase } from 'rxdb';
import { addRxPlugin } from 'rxdb';
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration';

// IndexedDB storage
import { getRxStoragePouch, addPouchPlugin } from 'rxdb/plugins/pouchdb';
import PouchdbAdapterIdb from 'pouchdb-adapter-idb';

addRxPlugin(RxDBMigrationPlugin);

addPouchPlugin(PouchdbAdapterIdb);

let db = null;
let dbPromise = null;
let collections = {};

export function getCollection(dbInstance, collectionName) {
  const collection = collections[collectionName] ||
    (dbInstance && dbInstance.collections && dbInstance.collections[collectionName]) ||
    (dbInstance && dbInstance[collectionName]);
  if (!collection) throw new Error(`RxDB collection not found: ${collectionName}`);
  return collection;
}

export async function getDB() {
  if (dbPromise) return dbPromise;
  if (db) return db;

  dbPromise = (async () => {
    const database = await createRxDatabase({
      name: 'schooldock_exam_db',
      storage: getRxStoragePouch('idb'),
      password: 'SCHOOLDOCK_EXAM_SECURE_KEY',
      multiInstance: false,
      ignoreDuplicate: true
    });

    collections = await database.addCollections({
    schoolquestions: {
      schema: {
        title: 'schoolquestions',
        version: 4,
        primaryKey: 'id',
        type: 'object',

        properties: {
          id: { type: 'string' },
          ownerId: { type: ['string', 'null'], index: true },
          SNo: { type: 'number' },
          QuestionId: { type: 'number' },

          // 🔐 Encrypted automatically
          Question: { type: 'string', encrypted: true },
          OptionA: { type: 'string', encrypted: true },
          OptionB: { type: 'string', encrypted: true },
          OptionC: { type: 'string', encrypted: true },
          OptionD: { type: 'string', encrypted: true },

          OptionAId: { type: 'number' },
          OptionBId: { type: 'number' },
          OptionCId: { type: 'number' },
          OptionDId: { type: 'number' },
          examCode: { type: 'string' },
          examHeader: { type: 'object' },

          SelectedAnswerId: { type: ['number', 'null'], encrypted: true },
          TimeSpent: { type: 'number' },
          Synced: { type: 'boolean' },
          syncStatus: { type: 'string', enum: ['pending', 'syncing', 'synced', 'failed'] },
          syncError: { type: ['string', 'null'] },
          syncAttempts: { type: 'number' },
          lastSyncAttempt: { type: ['string', 'null'] },
          nextRetryAt: { type: ['string', 'null'] },
          lastSyncedAt: { type: ['string', 'null'] },
          clientUpdatedAt: { type: 'string' },
          updatedAt: { type: 'string' }
        },

        required: ['id', 'SNo', 'QuestionId', 'syncStatus', 'updatedAt'],
        indexes: ['SNo', 'Synced', 'syncStatus']
      },
      migrationStrategies: {
        1: oldDoc => ({
          ...oldDoc,
          syncStatus: oldDoc.Synced ? 'synced' : 'pending',
          syncError: null,
          updatedAt: new Date().toISOString()
        }),
        2: oldDoc => ({
          ...oldDoc,
          examCode: oldDoc.examCode || '',
          examHeader: oldDoc.examHeader || {}
        }),
        3: oldDoc => ({
          ...oldDoc,
          syncAttempts: oldDoc.syncAttempts || 0,
          lastSyncAttempt: oldDoc.lastSyncAttempt || null,
          nextRetryAt: oldDoc.nextRetryAt || null,
          lastSyncedAt: oldDoc.lastSyncedAt || null,
          clientUpdatedAt: oldDoc.clientUpdatedAt || oldDoc.updatedAt || new Date().toISOString()
        }),
        4: oldDoc => ({
          ...oldDoc,
          ownerId: oldDoc.ownerId || null
        })
      }
    },

    cbtquestions: {
      schema: {
        title: 'cbtquestions',
        version: 4,
        primaryKey: 'id',
        type: 'object',
        properties: {
          id: { type: 'string' },
          ownerId: { type: ['string', 'null'], index: true },
          entityKey: { type: 'string', index: true },
          data: { type: 'object' },
          payload: { type: 'object' },
          operation: { type: 'string', enum: ['create', 'update', 'delete'] },
          backendId: { type: ['number', 'null'] },
          syncStatus: { type: 'string', enum: ['pending', 'syncing', 'synced', 'failed'] },
          syncError: { type: ['string', 'null'] },
          syncAttempts: { type: 'number' },
          lastSyncAttempt: { type: ['string', 'null'] },
          nextRetryAt: { type: ['string', 'null'] },
          lastSyncedAt: { type: ['string', 'null'] },
          clientUpdatedAt: { type: 'string' },
          updatedAt: { type: 'string' }
        },
        required: ['id', 'entityKey', 'data', 'payload', 'operation', 'syncStatus', 'updatedAt'],
        indexes: ['entityKey', 'syncStatus', 'updatedAt']
      },
      migrationStrategies: {
        1: oldDoc => oldDoc,
        2: oldDoc => ({
          ...oldDoc,
          operation: oldDoc.operation || 'update'
        }),
        3: oldDoc => ({
          ...oldDoc,
          syncAttempts: oldDoc.syncAttempts || 0,
          lastSyncAttempt: oldDoc.lastSyncAttempt || null,
          nextRetryAt: oldDoc.nextRetryAt || null,
          lastSyncedAt: oldDoc.lastSyncedAt || null,
          clientUpdatedAt: oldDoc.clientUpdatedAt || oldDoc.updatedAt || new Date().toISOString()
        }),
        4: oldDoc => ({
          ...oldDoc,
          ownerId: oldDoc.ownerId || null
        })
      }
    },

    cbtsubmissions: {
      schema: {
        title: 'cbtsubmissions',
        version: 2,
        primaryKey: 'id',
        type: 'object',
        properties: {
          id: { type: 'string' },
          ownerId: { type: ['string', 'null'], index: true },
          entityKey: { type: 'string', index: true },
          payload: { type: 'object' },
          syncStatus: { type: 'string', enum: ['pending', 'syncing', 'synced', 'failed'] },
          syncError: { type: ['string', 'null'] },
          syncAttempts: { type: 'number' },
          lastSyncAttempt: { type: ['string', 'null'] },
          nextRetryAt: { type: ['string', 'null'] },
          lastSyncedAt: { type: ['string', 'null'] },
          clientUpdatedAt: { type: 'string' },
          updatedAt: { type: 'string' }
        },
        required: ['id', 'entityKey', 'payload', 'syncStatus', 'updatedAt'],
        indexes: ['entityKey', 'syncStatus']
      },
      migrationStrategies: {
        1: oldDoc => ({
          ...oldDoc,
          syncAttempts: oldDoc.syncAttempts || 0,
          lastSyncAttempt: oldDoc.lastSyncAttempt || null,
          nextRetryAt: oldDoc.nextRetryAt || null,
          lastSyncedAt: oldDoc.lastSyncedAt || null,
          clientUpdatedAt: oldDoc.clientUpdatedAt || oldDoc.updatedAt || new Date().toISOString()
        }),
        2: oldDoc => ({
          ...oldDoc,
          ownerId: oldDoc.ownerId || null
        })
      }
    },

    offlineEntities: {
      schema: {
        title: 'offlineEntities',
        version: 1,
        primaryKey: 'id',
        type: 'object',
        properties: {
          id: { type: 'string' },
          entityType: { type: 'string', index: true },
          ownerId: { type: ['string', 'null'], index: true },
          data: { type: 'object' },
          operation: { type: 'string', enum: ['create', 'update', 'delete'] },
          syncStatus: { type: 'string', enum: ['pending', 'syncing', 'synced', 'failed'] },
          syncAttempts: { type: 'number' },
          lastSyncAttempt: { type: ['string', 'null'] },
          lastSyncedAt: { type: ['string', 'null'] },
          nextRetryAt: { type: ['string', 'null'] },
          syncError: { type: ['string', 'null'] },
          serverId: { type: ['string', 'number', 'null'] },
          serverUpdatedAt: { type: ['string', 'null'] },
          version: { type: ['number', 'null'] },
          clientUpdatedAt: { type: 'string' },
          updatedAt: { type: 'string' }
        },
        required: ['id', 'entityType', 'data', 'operation', 'syncStatus', 'clientUpdatedAt', 'updatedAt'],
        indexes: ['entityType', 'syncStatus', 'updatedAt']
      },
      migrationStrategies: {
        1: oldDoc => ({ ...oldDoc, ownerId: oldDoc.ownerId || null })
      }
    },

    syncOperations: {
      schema: {
        title: 'syncOperations',
        version: 2,
        primaryKey: 'id',
        type: 'object',
        properties: {
          id: { type: 'string' },
          operationType: { type: 'string' },
          ownerId: { type: ['string', 'null'], index: true },
          entityKey: { type: 'string', index: true },
          method: { type: 'string' },
          url: { type: 'string' },
          payload: { type: 'object' },
          headers: { type: 'object' },
          syncStatus: { type: 'string', enum: ['pending', 'syncing', 'synced', 'failed'] },
          syncAttempts: { type: 'number' },
          lastSyncAttempt: { type: ['string', 'null'] },
          lastSyncedAt: { type: ['string', 'null'] },
          nextRetryAt: { type: ['string', 'null'] },
          syncError: { type: ['string', 'null'] },
          serverId: { type: ['string', 'number', 'null'] },
          collectionName: { type: ['string', 'null'] },
          localRecordId: { type: ['string', 'null'] },
          sequence: { type: 'number' },
          clientUpdatedAt: { type: 'string' },
          createdAt: { type: 'string' }
        },
        required: [
          'id', 'operationType', 'entityKey', 'method', 'url', 'payload', 'headers',
          'syncStatus', 'syncAttempts', 'sequence', 'clientUpdatedAt', 'createdAt'
        ],
        indexes: ['entityKey', 'syncStatus', 'nextRetryAt']
      },
      migrationStrategies: {
        1: oldDoc => ({
          ...oldDoc,
          collectionName: oldDoc.collectionName || null,
          localRecordId: oldDoc.localRecordId || null,
          sequence: oldDoc.sequence || 0
        }),
        2: oldDoc => ({
          ...oldDoc,
          ownerId: oldDoc.ownerId || null
        })
      }
    }
    });

    Object.keys(collections).forEach(collectionName => {
      if (!database[collectionName]) database[collectionName] = collections[collectionName];
    });
    db = database;
    return db;
  })().catch(error => {
    db = null;
    dbPromise = null;
    collections = {};
    throw error;
  });

  return dbPromise;
}
